// كل ما يراه المستخدم بالعربية؛ رموز الـAPI وقاعدة البيانات تبقى إنجليزية.
export const CHANNELS = [
  { code: 'PHONE_CALL', ar: 'مكالمة' },
  { code: 'WHATSAPP', ar: 'WhatsApp' },
  { code: 'PAGE_MESSAGE', ar: 'رسالة من الصفحة' },
  { code: 'WEBSITE_REQUEST', ar: 'طلب من موقع الشركة' },   // قرار المالك 01/10/2026 (0134)
] as const;

export const RESULTS = [
  { code: 'ANSWERED', ar: 'تم الرد' },
  { code: 'NO_ANSWER', ar: 'لم يرد' },
  { code: 'UNREACHABLE', ar: 'الرقم مغلق أو غير متاح' },
  { code: 'CALL_BACK_LATER', ar: 'طلب التواصل لاحقًا' },
  { code: 'NOT_INTERESTED', ar: 'غير مهتم' },
  { code: 'INTERESTED', ar: 'مهتم' },
  { code: 'LOST_CUSTOMER', ar: 'خسرنا العميل' },   // قرار المالك 01/10/2026 (0152)
  { code: 'COMPANY_CUSTOMER', ar: 'عميل الشركة' },  // قرار المالك 01/10/2026 (0153) — عميلٌ حجز معنا قبلًا
  { code: 'OTHER', ar: 'أخرى' },
] as const;

export const STATUSES = [
  { code: 'NEW', ar: 'جديد' },
  { code: 'INTERESTED', ar: 'مهتم' },
  { code: 'READY_TO_BOOK', ar: 'جاهز للحجز' },
  { code: 'FOLLOW_UP', ar: 'متابعة' },
  { code: 'NOT_INTERESTED', ar: 'غير مهتم' },
  { code: 'BOOKED', ar: 'تم الحجز' },
  { code: 'LOST', ar: 'فقدنا العميل' },
  { code: 'COMPANY_CUSTOMER', ar: 'عميل الشركة' },  // 0153
] as const;

export const PRIORITIES = [
  { code: 'NORMAL', ar: 'عادية' },
  { code: 'IMPORTANT', ar: 'مهمة' },
  { code: 'URGENT', ar: 'عاجلة' },
] as const;

export const OPERATION_TYPES = [
  { code: 'NEW', ar: 'جديد' },
  { code: 'REPEAT', ar: 'مكرر' },
  { code: 'COMPANY', ar: 'شركة' },
  { code: 'CANCELLATION', ar: 'الغاء' },
  { code: 'MODIFICATION', ar: 'تعديل' },
  // «حجز مبدئي» (0140، قرار المالك 01/10/2026): لا يُعدّ جديدًا، والعمولة 50% من المكسب، وبلا تكلفة من البيع ناقص 3% (02/10/2026)
  { code: 'PRELIMINARY', ar: 'حجز مبدئي' },
] as const;

type Opt = { code: string; ar: string };
const lookup = (list: readonly Opt[]) => (code: string | null | undefined): string =>
  (code && list.find(o => o.code === code)?.ar) || '—';

export const channelAr = lookup(CHANNELS);
/**
 * **لا متابعة بلا يومٍ تالٍ** (قرار المالك 01/10/2026: «متخلّيش البياع يقفل تسجيل المتابعة إلا لو حاطط تاريخ يتابع
 * معاها تاني… الحالة الوحيدة اللي ميسجّلش فيها تاريخ إنه يكون فقدنا عميل أو غير مهتم»). نفس قاعدة الخادم (crm.ts).
 */
export const noNextNeeded = (status: string, result: string): boolean =>
  status === 'LOST' || status === 'NOT_INTERESTED' || status === 'COMPANY_CUSTOMER'
  || result === 'LOST_CUSTOMER' || result === 'NOT_INTERESTED' || result === 'COMPANY_CUSTOMER';
/**
 * **«عميل الشركة» تملأ الخانة التانية** (0153، قرار المالك 01/10/2026: «لو اخترناها في حاجة تتعمل تلقائي في الخانة
 * التانية»): تغيير النتيجة أو الموقف إليها يضعها في الأخرى؛ وتركُها من إحداهما يُفرغ الأخرى لتُختار من جديد.
 */
export function syncCompanyCustomer<T extends { result: string; customerStatus: string }>(form: T, changed: 'result' | 'customerStatus', value: string): T {
  const next = { ...form, [changed]: value };
  const other = changed === 'result' ? 'customerStatus' : 'result';
  if (value === 'COMPANY_CUSTOMER') return { ...next, [other]: 'COMPANY_CUSTOMER' };
  if (form[changed] === 'COMPANY_CUSTOMER' && form[other] === 'COMPANY_CUSTOMER') return { ...next, [other]: '' };
  return next;
}
export const NEXT_REQUIRED_MSG = 'حدّد يوم المتابعة القادمة — هتكلّم العميل تاني إمتى؟ (مش لازم بس لو غير مهتم أو فقدناه)';

/**
 * **مصدر المتابعة** (قرار المالك 01/10/2026: «لو تسجيل المتابعة جديد وضّح مصدره إنه جديد، زي ما وضّحنا إنه من بنك الأرقام،
 * ولو من طلب موقع هنكتب طلب موقع، ولو شات هنكتب شات الموقع»). يشتقّه الخادم من سلسلة العميل.
 */
export const SOURCES = [
  { code: 'LEAD_BANK', ar: 'بنك الأرقام', icon: '📞', tone: 'bg-cyan-100 text-cyan-800' },
  { code: 'WEB_LEAD', ar: 'طلب موقع', icon: '🌐', tone: 'bg-indigo-100 text-indigo-800' },
  { code: 'SITE_CHAT', ar: 'شات الموقع', icon: '💬', tone: 'bg-violet-100 text-violet-800' },
  // كان «🆕 تسجيل جديد» فقُرئ «عملاء جدد» (المالك 04/10/2026 عن كارت عمار: «بيقولى انه مسجلش 17 جديد اليوم») — وهو **مصدر**
  // التواصل لا جِدّةُ العميل: رقمٌ كلّمه من مصادر التواصل (تليفون/واتساب/فيسبوك) لا من بنك الأرقام ولا من الموقع، ومنه
  // تحديثاتُ موقفٍ لعملاء قدامى. و«منها تحديث موقف N» في الكارت هي التي تفصل الجديد فعلًا.
  { code: 'MANUAL', ar: 'من مصادر التواصل', icon: '📲', tone: 'bg-slate-100 text-ink-700' },
] as const;
export const sourceInfo = (code: string | null | undefined) => SOURCES.find(s => s.code === code) ?? null;

export const resultAr = lookup(RESULTS);
export const statusAr = lookup(STATUSES);
export const priorityAr = lookup(PRIORITIES);
export const operationAr = lookup(OPERATION_TYPES);

/** ألوان هادئة للحالات — مؤشرات بصرية بلا مبالغة */
export const statusTone: Record<string, string> = {
  NEW: 'bg-slate-100 text-ink-700',
  INTERESTED: 'bg-brand-50 text-brand-700',
  READY_TO_BOOK: 'bg-emerald-50 text-emerald-800',
  FOLLOW_UP: 'bg-amber-50 text-amber-800',
  NOT_INTERESTED: 'bg-slate-100 text-ink-500',
  COMPANY_CUSTOMER: 'bg-violet-50 text-violet-800',
  BOOKED: 'bg-emerald-600 text-white',
  LOST: 'bg-rose-50 text-rose-700',
};
export const priorityTone: Record<string, string> = {
  NORMAL: 'bg-slate-100 text-ink-700',
  IMPORTANT: 'bg-amber-100 text-amber-900',
  URGENT: 'bg-rose-100 text-rose-800',
};

/**
 * **المسمّى الوظيفي** (0189، قرار المالك 05/10/2026: «عاوز اسمى نفسى CEO and Founder، و محمد ابراهيم General Manager، و الباقى
 * كلهم Senior Ticketing Agent بدل كلمة Sales»). الأصل عمود `users.job_title` يأتي من الخادم؛ وهذا الاحتياط لصفوفٍ لا تحمله
 * (كشوف المرتب القديمة): المالك مؤسّس، وغيره وكيل تذاكر أوّل — ومحمد إبراهيم يأتي مسمّاه من الخادم دائمًا.
 */
export function jobTitleOf(u: { jobTitle?: string | null; isOwner?: boolean; role?: string }): string {
  if (u.jobTitle) return u.jobTitle;
  if (u.isOwner) return 'CEO and Founder';
  return u.role === 'ADMIN' ? 'Admin' : 'Senior Ticketing Agent';
}
